import React, { useEffect, useMemo, useState } from "react";
import {
  FileText,
  Download,
  Calendar,
  ShieldCheck,
  BarChart3,
} from "lucide-react";
import "./index.css";

const BASE =
  process.env.REACT_APP_URL || "http://127.0.0.1:8000";

const API_KEY = process.env.REACT_APP_API_KEY;

const apiHeaders = () => ({
  "Content-Type": "application/json",
  ...(API_KEY ? { "x-api-key": API_KEY } : {}),
  "ngrok-skip-browser-warning": "true",
});


/* =====================================================
   FETCH CMS CATEGORIES
   ===================================================== */

async function fetchFinancialCategories() {
  const res = await fetch(`${BASE}/api/cms/categories/`, {
    headers: apiHeaders(),
  });

  if (!res.ok) {
    throw new Error("Failed to fetch financial categories");
  }

  const data = await res.json();

  return Array.isArray(data)
    ? data
    : data.results || [];
}


/* =====================================================
   FORMAT DATE
   ===================================================== */

function formatDate(dateString) {
  if (!dateString) {
    return "—";
  }

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return date
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .toUpperCase();
}


/* =====================================================
   COMPONENT
   ===================================================== */

export default function FinancialInfo() {

  /*
   * activeCategory:
   *   "lcb"
   *   "financial"
   *
   * activePeriod:
   *   "monthly"
   *   "yearly"
   *   "half-yearly"
   */

  const [activeCategory, setActiveCategory] = useState("lcb");
  const [activePeriod, setActivePeriod] = useState("monthly");

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");


  /* =====================================================
     FETCH DATA
     ===================================================== */

  useEffect(() => {
    let mounted = true;

    setLoading(true);
    setError("");

    fetchFinancialCategories()
      .then((data) => {
        if (mounted) {
          setCategories(data);
        }
      })
      .catch((err) => {
        console.error(err);

        if (mounted) {
          setCategories([]);
          setError("Unable to load financial documents.");
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);


  /* =====================================================
     CATEGORY HELPERS
     ===================================================== */

  const isLcb = activeCategory === "lcb";


  /*
   * Find the API category according to
   * the currently selected tab + period.
   */

  const selectedCategory = useMemo(() => {

    let slug = "";

    if (activeCategory === "lcb") {

      if (activePeriod === "monthly") {
        slug = "lcb-monthly";
      }
      if(activePeriod === "half-yearly") {
        slug = "lcb-halfyearly";
      }
      if (activePeriod === "quarterly") {
        slug = "lcb-quarterly";
      }


      if (activePeriod === "yearly") {
        slug = "lcb-yearly";
      }

    } else {
      if(activePeriod === "monthly") {
        slug = "financials-monthly";
      }

      if (activePeriod === "half-yearly") {
        slug = "financials-halfyearly";
      }
      if (activePeriod === "quarterly") {
        slug = "financials-quarterly";
      }

      if (activePeriod === "yearly") {
        slug = "financials-yearly";
      }

    }

    return categories.find(
      (category) => category.slug === slug
    );

  }, [
    categories,
    activeCategory,
    activePeriod,
  ]);


  /* =====================================================
     DOCUMENTS
     ===================================================== */

  const documents = useMemo(() => {

    if (!selectedCategory) {
      return [];
    }

    const docs = (selectedCategory.documents || [])
      .filter((doc) => doc.is_active !== false)
      .slice();

    /*
     * Latest first
     */

    docs.sort((a, b) => {

      const dateA = new Date(a.period_date || a.created_at);
      const dateB = new Date(b.period_date || b.created_at);

      return dateB - dateA;

    });

    return docs;

  }, [selectedCategory]);


  /* =====================================================
     HEADER
     ===================================================== */

  const headTitle = isLcb
    ? "Liquid Capital Balance (LCB)"
    : "Financial Statements";

  const headDesc = isLcb
    ? "Access our Liquid Capital Balance statements for transparency and compliance."
    : "Access our audited financial statements for transparency and compliance.";


  /* =====================================================
     PERIOD OPTIONS
     ===================================================== */

  const periodOptions = isLcb
    ? [
        {
          key: "monthly",
          label: "Monthly",
        },
        {
          key: "half-yearly",
          label: "Half-Yearly",
        },
                {
          key: "quarterly",
          label: "Quarterly",
        },
        {
          key: "yearly",
          label: "Annually",
        },
      ]
    : [
        {
          key: "monthly",
          label: "Monthly",
        },
        {
          key: "half-yearly",
          label: "Half-Yearly",
        },
                {
          key: "quarterly",
          label: "Quarterly",
        },
        {
          key: "yearly",
          label: "Annually",
        },
      ];


  /* =====================================================
     CATEGORY CHANGE
     ===================================================== */

  const selectCategory = (category) => {

    setActiveCategory(category);

    if (category === "lcb") {
      setActivePeriod("monthly");
    } else {
      setActivePeriod("half-yearly");
    }

  };


  /* =====================================================
     RENDER
     ===================================================== */

  return (
    <div className="fin-page">
      <div className="fin-wrap">
        <div className="fin-shell">

          {/* =================================================
              HEADER
              ================================================= */}

          <div className="fin-head">

            <div className="fin-head-ic">
              <BarChart3 size={26} />
            </div>

            <div>

              <h1 className="fin-head-title">
                {headTitle}
              </h1>

              <p className="fin-head-desc">
                {headDesc}
              </p>

            </div>

          </div>


          {/* =================================================
              CATEGORY TABS
              ================================================= */}

          <div className="fin-tabs">

            <button
              className={`fin-tab ${
                !isLcb ? "active" : ""
              }`}
              onClick={() =>
                selectCategory("financial")
              }
            >
              <FileText size={17} />

              Financial Statements
            </button>


            <button
              className={`fin-tab ${
                isLcb ? "active" : ""
              }`}
              onClick={() =>
                selectCategory("lcb")
              }
            >
              <BarChart3 size={17} />

              Liquid Capital Balance (LCB)
            </button>

          </div>


          {/* =================================================
              PERIOD TOGGLE
              ================================================= */}

          <div className="fin-period">

            {periodOptions.map((option) => (

              <button
                key={option.key}
                className={`fin-period-btn ${
                  activePeriod === option.key
                    ? "active"
                    : ""
                }`}
                onClick={() =>
                  setActivePeriod(option.key)
                }
              >

                <Calendar size={15} />

                {option.label}

              </button>

            ))}

          </div>


          {/* =================================================
              LOADING
              ================================================= */}

          {loading && (

            <div className="fin-empty">
              Loading documents...
            </div>

          )}


          {/* =================================================
              ERROR
              ================================================= */}

          {!loading && error && (

            <div className="fin-empty">
              {error}
            </div>

          )}


          {/* =================================================
              DOCUMENTS
              ================================================= */}

          {!loading &&
            !error &&
            documents.length > 0 && (

              <div className="fin-grid">

                {documents.map((doc, index) => (

                  <div
                    key={doc.id}
                    className="fin-card"
                  >

                    {/* LATEST */}

                    {index === 0 && (

                      <span className="fin-ribbon">
                        LATEST
                      </span>

                    )}


                    {/* ICON */}

                    <div className="fin-card-ic">
                      <FileText size={22} />
                    </div>


                    {/* TITLE */}

                    <h3 className="fin-card-title">
                      {doc.title || "Financial Document"}
                    </h3>


                    <span className="fin-card-underline" />


                    {/* DESCRIPTION */}

                    <div className="fin-card-sub">
                      {doc.description || ""}
                    </div>


                    {/* DATE */}

                    <div className="fin-card-date">

                      <Calendar size={15} />

                      {formatDate(doc.period_date)}

                    </div>


                    {/* ACTIONS */}

                    <div className="fin-actions">

                      <a
                        href={doc.file_url || doc.file}
                        target="_blank"
                        rel="noreferrer"
                        className="fin-dl"
                      >

                        <Download size={16} />

                        DOWNLOAD FILE

                      </a>


                      <a
                        href={doc.file_url || doc.file}
                        target="_blank"
                        rel="noreferrer"
                        className="fin-pdf"
                      >

                        PDF

                        <span className="fin-pdf-badge">
                          PDF
                        </span>

                      </a>

                    </div>

                  </div>

                ))}

              </div>

            )}


          {/* =================================================
              NO DOCUMENTS
              ================================================= */}

          {!loading &&
            !error &&
            documents.length === 0 && (

              <div className="fin-empty">

                No documents found for this selection.

              </div>

            )}


          {/* =================================================
              IMPORTANT INFORMATION
              ================================================= */}

          <div className="fin-info">

            <div className="fin-info-ic">
              <ShieldCheck size={26} />
            </div>

            <div>

              <h4 className="fin-info-title">
                Important Information
              </h4>

              <p className="fin-info-text">
                These statements are prepared in
                accordance with regulatory requirements.
                For any queries, please contact our
                compliance department.
              </p>

            </div>

          </div>

        </div>
      </div>
    </div>
  );
}
